// ============================================
// ROLC OneROLC - Survey Page
// Version: 3.6.0
// ============================================

import { getToken, getName } from '../utils/storage.js';
import { $, setContent } from '../utils/domHelpers.js';
import { escapeHtml, stripHtml, showToast } from '../utils.js';
import { getEvents, submitSurvey } from '../api/endpoints.js';
import { navigateToHome } from '../router/routes.js';
import { getSurveys } from '../api/endpoints.js';
import { queueSubmission } from '../services/queueService.js';

// ============================================
// Date Picker Helper
// ============================================
function setupDatePicker(btn, hiddenInput, format) {
    const today = new Date();
    const currentYear = today.getFullYear();
    const years = Array.from({ length: 111 }, (_, i) => currentYear - 100 + i);
    const months = [
        { v: '01', l: 'January' }, { v: '02', l: 'February' }, { v: '03', l: 'March' },
        { v: '04', l: 'April' }, { v: '05', l: 'May' }, { v: '06', l: 'June' },
        { v: '07', l: 'July' }, { v: '08', l: 'August' }, { v: '09', l: 'September' },
        { v: '10', l: 'October' }, { v: '11', l: 'November' }, { v: '12', l: 'December' }
    ];
    const days = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));

    const monthOptions = months.map(m => `<option value="${m.v}"${m.v === String(today.getMonth()+1).padStart(2,'0') ? ' selected' : ''}>${m.l}</option>`).join('');
    const dayOptions = days.map(d => `<option value="${d}"${d === String(today.getDate()).padStart(2,'0') ? ' selected' : ''}>${d}</option>`).join('');
    const yearOptions = years.map(y => `<option value="${y}"${y === currentYear ? ' selected' : ''}>${y}</option>`).join('');

    const picker = document.createElement('div');
    picker.className = 'date-picker-dropdown';
    picker.style.cssText = 'position:absolute;background:var(--white);border:2px solid #e2e8f0;border-radius:8px;padding:12px;margin-top:4px;display:none;z-index:50;box-shadow:0 4px 12px rgba(0,0,0,0.15);min-width:280px;';
    picker.innerHTML = `
        <div style="display:flex;gap:6px;justify-content:space-between;margin-bottom:8px;">
            <select class="dp-month" style="flex:1;padding:10px;border:2px solid #d1d5db;border-radius:6px;font-size:0.9rem;background:var(--white);">${monthOptions}</select>
            <select class="dp-day" style="flex:1;padding:10px;border:2px solid #d1d5db;border-radius:6px;font-size:0.9rem;background:var(--white);">${dayOptions}</select>
            <select class="dp-year" style="flex:1;padding:10px;border:2px solid #d1d5db;border-radius:6px;font-size:0.9rem;background:var(--white);">${yearOptions}</select>
        </div>
        <button type="button" class="dp-ok" style="width:100%;padding:10px;background:linear-gradient(135deg,var(--primary) 0%,var(--primary-light) 100%);color:white;border:none;border-radius:6px;font-size:0.9rem;font-weight:700;cursor:pointer;">OK</button>
    `;
    btn.parentNode.style.position = 'relative';
    btn.parentNode.appendChild(picker);

    const formatDate = (m, d, y) => {
        switch (format) {
            case 'dd/mm/yyyy': return `${d}/${m}/${y}`;
            case 'yyyy/mm/dd': return `${y}/${m}/${d}`;
            case 'mm-dd-yyyy': return `${m}-${d}-${y}`;
            case 'dd-mm-yyyy': return `${d}-${m}-${y}`;
            case 'yyyy-mm-dd': return `${y}-${m}-${d}`;
            case 'mm/dd/yyyy': default: return `${m}/${d}/${y}`;
        }
    };

    const applySelection = () => {
        const m = picker.querySelector('.dp-month').value;
        const d = picker.querySelector('.dp-day').value;
        const y = picker.querySelector('.dp-year').value;
        const iso = `${y}-${m}-${d}`;
        hiddenInput.value = iso;
        btn.textContent = formatDate(m, d, y);
        picker.style.display = 'none';
    };

    btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = picker.style.display === 'block';
        document.querySelectorAll('.date-picker-dropdown').forEach(p => p.style.display = 'none');
        picker.style.display = isOpen ? 'none' : 'block';
    });

    picker.querySelector('.dp-ok').addEventListener('click', (e) => {
        e.stopPropagation();
        applySelection();
    });

    document.addEventListener('click', (e) => {
        if (!btn.contains(e.target) && !picker.contains(e.target)) {
            picker.style.display = 'none';
        }
    });
}

// ============================================
// Render Survey Page
// ============================================
export async function renderSurveyPage(params = {}) {
    const surveyName = params.name;
    const token = getToken();
    const name = getName();

    if (!surveyName) {
        navigateToHome();
        return;
    }

    setContent(`
        <div class="card">
            <div class="welcome-box">
                <div class="welcome-label">Loading survey...</div>
            </div>
            <p style="text-align:center;color:#6b7280;">Please wait</p>
        </div>
    `);

    try {
        // Get survey description
        const surveysData = await getSurveys(token);
        const survey = surveysData.surveys?.find(s => s.name === surveyName);
        const description = survey?.description || '';

        // Get events
        const eventsData = await getEvents(surveyName);
        const events = eventsData.events || [];

        if (events.length === 0) {
            setContent(`
                <div class="card">
                    <div class="error-box">
                        <div class="error-icon">📝</div>
                        <p class="error-text">This survey has no questions yet.</p>
                        <button class="btn btn-secondary" id="btnBackHome">Back to Home</button>
                    </div>
                </div>
            `);
            document.getElementById('btnBackHome')?.addEventListener('click', navigateToHome);
            return;
        }

        renderSurveyForm(surveyName, description, events, token, name);
    } catch (err) {
        showToast('Unable to load survey: ' + err.message, 'error');
        navigateToHome();
    }
}

// ============================================
// Render Survey Form
// ============================================
function renderSurveyForm(surveyName, description, events, token, name) {
    let html = `
        <div class="card">
            <div class="welcome-box">
                <div class="welcome-label">Welcome, ${escapeHtml(name.charAt(0).toUpperCase() + name.slice(1))}</div>
            </div>
            <h2 class="card-title">${escapeHtml(surveyName)}</h2>
            ${description ? `<p class="survey-desc">${escapeHtml(description)}</p>` : ''}
            <form id="surveyForm">
    `;

    events.forEach((ev, idx) => {
        const required = ev.required !== false;
        html += `
            <div class="question-item">
                <label style="display:block;margin-bottom:8px;font-weight:600;white-space:pre-wrap;">${escapeHtml(stripHtml(ev.name))}${required ? ' <span class="required-star">*</span>' : ''}</label>
        `;

        if (ev.type === 'text') {
            html += `<input type="text" id="event-${idx}" placeholder="Enter your answer" style="width:100%;padding:12px;border:2px solid #d1d5db;border-radius:8px;font-size:1rem;">`;
        } else if (ev.type === 'rating') {
            html += `<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">`;
            // Rating: 10 down to 0
            for (let i = ev.maxSelect; i >= 0; i--) {
                const label = i === 10 ? '10 (Highest)' : (i === 0 ? '0 (Lowest)' : String(i));
                html += `<label style="display:flex;align-items:center;gap:4px;"><input type="radio" name="event-${idx}" value="${i}" style="margin:0;">${label}</label>`;
            }
            html += `</div>`;
        } else if (ev.type === 'multi') {
            html += `<div class="checkbox-group">`;
            ev.options.forEach((opt, optIdx) => {
                html += `<label style="display:block;margin:4px 0;"><input type="checkbox" name="event-${idx}" value="${escapeHtml(stripHtml(opt))}"> ${opt}</label>`;
            });
            html += `</div>`;
        } else if (ev.type === 'rank') {
            const ordinals = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];
            html += `<div class="rank-group">`;
            for (let r = 1; r <= ev.maxSelect; r++) {
                html += `<div class="rank-slot" style="margin-bottom:8px;">`;
                html += `<label class="rank-label">${ordinals[r-1]} Choice</label>`;
                html += `<select class="rank-select" name="event-${idx}-${r}" style="width:100%;padding:12px;border:2px solid #d1d5db;border-radius:8px;">`;
                html += `<option value="">-- Select --</option>`;
                ev.options.forEach(opt => {
                    html += `<option value="${escapeHtml(stripHtml(opt))}">${opt}</option>`;
                });
                html += `</select>`;
                html += `</div>`;
            }
            html += `</div>`;
        } else if (ev.type === 'date') {
            const dateFormat = ev.dateFormat || 'mm/dd/yyyy';
            const safeName = escapeHtml(surveyName);
            const safeFormat = escapeHtml(dateFormat);
            html += `<div class="date-picker-wrapper" data-format="${safeFormat}">`;
            html += `<button type="button" class="btn btn-secondary date-picker-btn" id="event-${idx}-btn" style="width:100%;text-align:left;">Select date...</button>`;
            html += `<input type="hidden" id="event-${idx}" name="event-${idx}" value="">`;
            html += `<p class="form-hint" style="margin-top:4px;font-size:0.85rem;color:var(--text-light);">Format: ${safeFormat}</p>`;
            html += `</div>`;
        } else if (ev.type === 'composite') {
            html += `<div class="composite-group" style="padding-left:12px;border-left:3px solid #e2e8f0;">`;
            if (ev.subQuestions && ev.subQuestions.length > 0) {
                ev.subQuestions.forEach((sub, subIdx) => {
                    const subRequired = sub.required !== false;
                    html += `
                        <div style="margin-bottom:14px;">
                            <label style="display:block;margin-bottom:6px;font-weight:500;color:#4b5563;">${escapeHtml(sub.label)}${subRequired ? ' <span class="required-star">*</span>' : ''}</label>
                    `;
                    const subId = `composite-${idx}-${subIdx}`;
                    if (sub.type === 'text') {
                        html += `<input type="text" id="${subId}" placeholder="Enter your answer" style="width:100%;padding:12px;border:2px solid #d1d5db;border-radius:8px;font-size:1rem;background:#f9fafb;">`;
                    } else if (sub.type === 'dropdown') {
                        html += `<select id="${subId}" style="width:100%;padding:12px;border:2px solid #d1d5db;border-radius:8px;background:#f9fafc;">`;
                        html += `<option value="">-- Select an option --</option>`;
                        if (sub.options && Array.isArray(sub.options)) {
                            sub.options.forEach(opt => {
                                html += `<option value="${escapeHtml(stripHtml(opt))}">${opt}</option>`;
                            });
                        }
                        html += `</select>`;
                    } else if (sub.type === 'date') {
                        const subDateFormat = sub.dateFormat || 'mm/dd/yyyy';
                        html += `<div class="date-picker-wrapper" data-format="${escapeHtml(subDateFormat)}">`;
                        html += `<button type="button" class="btn btn-secondary date-picker-btn" id="${subId}-btn" style="width:100%;text-align:left;">Select date...</button>`;
                        html += `<input type="hidden" id="${subId}" name="${subId}" value="">`;
                        html += `<p class="form-hint" style="margin-top:4px;font-size:0.85rem;color:var(--text-light);">Format: ${escapeHtml(subDateFormat)}</p>`;
                        html += `</div>`;
                    } else if (sub.type === 'rating') {
                        html += `<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">`;
                        for (let r = 10; r >= 0; r--) {
                            const label = r === 10 ? '10 (Highest)' : (r === 0 ? '0 (Lowest)' : String(r));
                            html += `<label style="display:flex;align-items:center;gap:4px;"><input type="radio" name="${subId}" value="${r}" style="margin:0;">${label}</label>`;
                        }
                        html += `</div>`;
                    } else {
                        html += `<input type="text" id="${subId}" placeholder="Enter your answer" style="width:100%;padding:12px;border:2px solid #d1d5db;border-radius:8px;font-size:1rem;background:#f9fafb;">`;
                    }
                    html += `</div>`;
                });
            }
            html += `</div>`;
        } else {
            // dropdown
            html += `<select id="event-${idx}" style="width:100%;padding:12px;border:2px solid #d1d5db;border-radius:8px;">`;
            html += `<option value="">-- Select an option --</option>`;
            ev.options.forEach(opt => {
                html += `<option value="${escapeHtml(stripHtml(opt))}">${opt}</option>`;
            });
            html += `</select>`;
        }
        html += `</div>`;  // closes question-item
    });

    html += `
                <button type="submit" class="btn btn-primary mt-2" id="submitSurveyBtn">Submit Survey</button>
            </form>
            <button class="btn btn-secondary mt-1" id="btnCancelSurvey">Cancel</button>
        </div>
    `;

    setContent(html);

    // Setup custom date pickers for date questions
    events.forEach((ev, idx) => {
        if (ev.type === 'date') {
            const btn = document.getElementById(`event-${idx}-btn`);
            const hiddenInput = document.getElementById(`event-${idx}`);
            if (!btn || !hiddenInput) return;

            setupDatePicker(btn, hiddenInput, ev.dateFormat || 'mm/dd/yyyy');
        } else if (ev.type === 'composite') {
            if (!ev.subQuestions || !Array.isArray(ev.subQuestions)) return;
            ev.subQuestions.forEach((sub, subIdx) => {
                if (sub.type !== 'date') return;
                const subId = `composite-${idx}-${subIdx}`;
                const btn = document.getElementById(`${subId}-btn`);
                const hiddenInput = document.getElementById(subId);
                if (!btn || !hiddenInput) return;
                setupDatePicker(btn, hiddenInput, sub.dateFormat || 'mm/dd/yyyy');
            });
        }
    });

    // Form submission
    document.getElementById('surveyForm').addEventListener('submit', async (e) => {
        e.preventDefault();
        await handleSurveySubmit(events, token, surveyName, name);
    });

    // Cancel button
    document.getElementById('btnCancelSurvey')?.addEventListener('click', navigateToHome);
}

// ============================================
// Handle Survey Submit
// ============================================
async function handleSurveySubmit(events, token, surveyName, name) {
    const submissions = [];
    let isValid = true;

    events.forEach((ev, idx) => {
        let value = null;
        const required = ev.required !== false;

        if (ev.type === 'text') {
            const input = document.getElementById(`event-${idx}`);
            value = input ? input.value.trim() : '';
        } else if (ev.type === 'rating') {
            const selected = document.querySelector(`input[name="event-${idx}"]:checked`);
            value = selected ? selected.value : null;
        } else if (ev.type === 'multi') {
            const selected = Array.from(document.querySelectorAll(`input[name="event-${idx}"]:checked`));
            value = selected.length > 0 ? selected.map(cb => cb.value) : null;
        } else if (ev.type === 'rank') {
            const values = [];
            for (let r = 1; r <= ev.maxSelect; r++) {
                const select = document.querySelector(`select[name="event-${idx}-${r}"]`);
                if (select && select.value) {
                    values.push(select.value);
                }
            }
            value = values.length > 0 ? values : null;
        } else if (ev.type === 'date') {
            const input = document.getElementById(`event-${idx}`);
            value = input ? input.value : '';
        } else if (ev.type === 'composite') {
            const compositeValue = {};
            if (ev.subQuestions && Array.isArray(ev.subQuestions)) {
                ev.subQuestions.forEach((sub, subIdx) => {
                    const subId = `composite-${idx}-${subIdx}`;
                    const subRequired = sub.required !== false;
                    if (sub.type === 'text') {
                        const input = document.getElementById(subId);
                        compositeValue[sub.label] = input ? input.value.trim() : '';
                    } else if (sub.type === 'dropdown') {
                        const select = document.getElementById(subId);
                        compositeValue[sub.label] = select ? select.value : '';
                    } else if (sub.type === 'date') {
                        const input = document.getElementById(subId);
                        compositeValue[sub.label] = input ? input.value : '';
                    } else if (sub.type === 'rating') {
                        const selected = document.querySelector(`input[name="${subId}"]:checked`);
                        compositeValue[sub.label] = selected ? selected.value : '';
                    } else {
                        const input = document.getElementById(subId);
                        compositeValue[sub.label] = input ? input.value.trim() : '';
                    }
                });
            }
            value = compositeValue;
        } else {
            // dropdown
            const select = document.getElementById(`event-${idx}`);
            value = select ? select.value : '';
        }

        // Validate required fields
        if (required) {
            if (ev.type === 'composite') {
                const emptySubs = ev.subQuestions?.filter(sub => sub.required !== false && !value[sub.label]) || [];
                if (emptySubs.length > 0) {
                    isValid = false;
                    showToast(`Please answer: ${emptySubs[0].label}`, 'error');
                }
            } else if (!value || (Array.isArray(value) && value.length === 0)) {
                isValid = false;
                showToast(`Please answer: ${stripHtml(ev.name)}`, 'error');
            }
        }

        submissions.push({ event: ev.name, choice: value });
    });

    if (!isValid) return;

    const submitBtn = document.getElementById('submitSurveyBtn');
    submitBtn.textContent = 'Submitting...';
    submitBtn.disabled = true;

    try {
        // Check if online
        if (!navigator.onLine) {
            await queueSubmission(token, submissions, surveyName);
            submitBtn.textContent = 'Submit Survey';
            submitBtn.disabled = false;
            setTimeout(() => navigateToHome(), 1500);
            return;
        }

        const data = await submitSurvey(token, submissions, surveyName);
        if (data.error) {
            // Check if error is duplicate
            if (data.error.includes('already completed')) {
                showToast('You have already completed this survey.', 'warning');
                setTimeout(() => navigateToHome(), 2000);
                submitBtn.textContent = 'Submit Survey';
                submitBtn.disabled = false;
                return;
            }
            showToast(data.error, 'error');
            submitBtn.textContent = 'Submit Survey';
            submitBtn.disabled = false;
            return;
        }

        showToast('Survey submitted successfully!', 'success');
        submitBtn.textContent = '✅ Submitted!';
        setTimeout(() => navigateToHome(), 2000);
    } catch (err) {
        // If network error, try to queue
        if (!navigator.onLine || err.message.includes('Network')) {
            await queueSubmission(token, submissions, surveyName);
            submitBtn.textContent = 'Submit Survey';
            submitBtn.disabled = false;
            setTimeout(() => navigateToHome(), 1500);
            return;
        }
        showToast('Submission failed: ' + err.message, 'error');
        submitBtn.textContent = 'Submit Survey';
        submitBtn.disabled = false;
    }
}


